import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import EmptyState from '@app/src/components/EmptyState'
import LinkButton from '@app/src/components/LinkButton'
import Table from '@app/src/components/Table'
import { useRiskSettings } from '@app/src/context/RiskSettingsContext'
import usePagination from '@app/src/hooks/pagination'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { CONFIGURATION_PAGE_IDS } from '@app/src/pages/Configurations/ConfigurationsScene'
import RiskScreeningProviderRisksFilters from '@app/src/pages/ResourceCollection/Filters/RiskScreeningProviderRisksFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import ResourceCollectionScene from '@app/src/pages/ResourceCollection/ResourceCollectionScene'
import type { Country, RiskStatus, RiskType } from '@app/src/types/resourceExplorer'
import { ResourceTypes } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import { TuneOutlined } from '@mui/icons-material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { generatePath } from 'react-router'

import HasWriteAccess from '@app/src/components/HasWriteAccess'
import type { Provider } from '@app/src/types/organizations'
import ProviderRisksPageHeader from './ProviderRisksPageHeader'
import ProviderRisksPageRow from './ProviderRisksPageRow'
import { RiskScreeningViews } from './RiskScreeningScene'

interface ProviderRisk {
  id: number
  riskTypeId: number
  riskType: RiskType
  context: string
  value: number
  status: RiskStatus
  country: Country
  providerId: number
  providerName: string
  parentObject: Provider
  targetObjectType: string
  targetObjectId: number
}

export interface ProviderRiskRow {
  id: number
  riskTypeId: number
  riskType: ProviderRisk['riskType']
  sustainabilityArea: string
  severity: RiskStatus
  context: string
  value: number
  status: RiskStatus
  providerId: number
  providerName: string
  parentObject: Provider
  targetObjectType: string
  targetObjectId: number
}

interface ProviderRisksPageProps {
  providerId?: number
  relatedContentFilter?: FilterGroup[]
}

const allowedFilters: string[] = [
  'riskTypeId',
  'riskType.name',
  'riskContext',
  'riskSustainabilityArea',
  'riskStatus',
  'country.id',
  'value',
]

const ProviderRisksPage: React.FC<ProviderRisksPageProps> = ({ providerId, relatedContentFilter = [] }) => {
  const filters = useGetApiQueryFilters(allowedFilters)
  const [pageNumber, itemsPerPage, setPage, setPageSize] = usePagination()
  const { sorting } = useSort()
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const { activeRiskSettings, isLoadingRiskSettings } = useRiskSettings()

  const basePayload = {
    filter: [...relatedContentFilter, ...filters],
    sort: sorting,
    include: [],
    pagination: {
      pageNumber,
      itemsPerPage,
    },
  }

  const rawExportPayload = {
    filter: relatedContentFilter,
    sort: sorting,
    pagination: {
      pageNumber,
      itemsPerPage,
    },
    include: [],
  }

  const {
    items: providerRisks,
    isLoading,
    isFetching,
    isError,
  } = useFetchCollectionWithPost<ProviderRisk>({
    key: FetchKey.RiskScreening,
    endpoint: endpoints.riskScreeningProviderRisks,
    payload: basePayload,
    options: {
      enabled: Boolean(activeRiskSettings.length && providerId),
    },
  })

  const riskRows = useMemo(() => {
    if (!providerRisks) return []

    return providerRisks.map(risk => ({
      id: risk.id,
      riskTypeId: risk.riskTypeId,
      riskType: risk.riskType,
      sustainabilityArea: (risk.riskType as ProviderRiskRow['riskType']).sustainabilityArea,
      severity: risk.status as RiskStatus,
      context: risk.context,
      value: risk.value,
      status: risk.status,
      providerId: risk.providerId,
      providerName: risk.providerName,
      parentObject: risk.parentObject,
      targetObjectType: risk.targetObjectType,
      targetObjectId: risk.targetObjectId,
    }))
  }, [providerRisks])

  const buttons: ActionButton[] = [
    {
      label: formatMessage({ id: 'resourceCollections.general.export' }),
      variant: 'outlined',
      onClick: () =>
        openDrawer(
          <DrawerViewExport
            resourceType={ResourceTypes.RiskScreening}
            resourceView={RiskScreeningViews.AllLocations}
            count={riskRows.length}
            userFilter={filters}
            rawExportPayload={rawExportPayload}
            disableExportSettings
          />,
        ),
      disabled: isLoading || riskRows.length === 0,
    },
  ]

  if (!activeRiskSettings.length) {
    return (
      <ResourceCollectionScene title={formatMessage({ id: 'navbar.riskScreening' })} actionButtons={buttons} embedded>
        <EmptyState
          iconComponent={TuneOutlined}
          title={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.title' })}
          description={formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.description' })}
        >
          <HasWriteAccess>
            <LinkButton
              variant="outlined"
              to={generatePath(paths.configurations, { configurationsPage: CONFIGURATION_PAGE_IDS.RiskScreening })}
            >
              {formatMessage({ id: 'dashboard.sourcing.countryRiskMap.emptyState.action' })}
            </LinkButton>
          </HasWriteAccess>
        </EmptyState>
      </ResourceCollectionScene>
    )
  }

  return (
    <ResourceCollectionScene
      title={formatMessage({ id: 'navbar.riskScreening' })}
      actionButtons={buttons}
      filter={
        providerId ? (
          <RiskScreeningProviderRisksFilters allowedFilters={allowedFilters} providerId={providerId} />
        ) : null
      }
      embedded
    >
      <Table<ProviderRiskRow>
        RowComponent={({ row }) => <ProviderRisksPageRow row={row} />}
        HeaderComponent={() => <ProviderRisksPageHeader />}
        data={riskRows}
        page={pageNumber}
        pageSize={itemsPerPage}
        setPage={setPage}
        setPageSize={setPageSize}
        isLoading={isLoading || isFetching || isLoadingRiskSettings}
        count={riskRows.length}
        isError={isError}
        stickyColumnIndex={0}
      />
    </ResourceCollectionScene>
  )
}

export default ProviderRisksPage
